/**
 * Pixi-backed ability-VFX manager (#577) — cast/impact flashes + ground-
 * target AOE shape indicators for the locked nine-ability list
 * (`abilityVisuals.ts`'s `ABILITY_VISUAL_DEFS_BY_SLUG`).
 *
 * STRUCTURAL SIBLING of `CombatTextManager` (#48): one manager owns a
 * dedicated set of pooled Pixi objects on the `fx` layer, spawns/animates/
 * reclaims them, and caps concurrency by evicting the oldest live instance.
 *
 * TWO SPAWN TRIGGERS, one per wire shape (#804):
 *
 *   1. The `cast`-EDGE path. `cast` is persistent per-tick STATE (non-null for
 *      as long as the caster is non-idle), so a CAST-TIME ability is spawned
 *      once when `(slot, startTick)` is NEWLY observed for an entity — at cast
 *      START, which is what telegraphs an incoming AOE (Meteor / Rain of
 *      Arrows wind up for ~1s). Not once per snapshot for an ongoing cast, and
 *      not per rendered frame.
 *   2. The `castEvents` path. An INSTANT (`castTimeTicks: 0`) starts AND
 *      resolves inside ONE `step`, so `castState` is `idle` again before the
 *      snapshot is serialized and `cast` is `null` in every snapshot that ever
 *      existed — there is NO edge for (1) to detect, and Frost Nova /
 *      Shadowfury / Cone of Cold (plus Spell Reflect / Bandage / Battleshout)
 *      rendered NOTHING AT ALL. The wire's one-shot `castFired` event is the
 *      only carrier of a same-tick cast; this manager spawns off it.
 *
 * NO DOUBLE-SPAWN, no dedupe bookkeeping: the event carries the cast's real
 * span, and path (2) fires ONLY when `startTick === firedTick` — precisely the
 * casts path (1) structurally cannot see. A cast-time ability's resolve event
 * is skipped (it already flashed at cast start), and a cast collapsed to
 * instant by #669's `nextCastInstant` is correctly treated as an instant
 * without any special case.
 *
 * Ingest takes the WHOLE snapshot because the two paths need entities +
 * castEvents from the SAME tick (an event's caster is resolved against that
 * snapshot's entity list for its championId/facing/position and its #422
 * stealth check).
 *
 * Two pooled Pixi objects per slot (a `Graphics` + a `Text`), both always
 * present, toggled by archetype: a ground shape (circle/wedge) draws only
 * the `Graphics`; a floating glyph draws only the `Text`; the speech-bubble
 * archetype (Battleshout) uses both. The `Graphics` shape itself is drawn
 * ONCE at spawn (local-origin geometry); `update()` only repositions +
 * re-alphas it per frame, mirroring how `CombatTextManager` repositions its
 * pooled `Text` without re-measuring it every frame.
 */

import { Graphics, Text, type Container } from "pixi.js";
import type { EntityId, WireSimState } from "@pit/protocol";
import {
  abilityVisualDefFor,
  advanceAbilityVisual,
  type AbilityVisualDef,
} from "../champions/abilityVisuals.js";
import {
  WORLD_TEXT_RESOLUTION,
  facingToScreenVec,
  projectRadius,
  worldToScreen,
  type ProjectionCenter,
} from "./projection.js";

type WireEntity = WireSimState["entities"][number];

/** Screen-space rise distance for a floating (glyph/shield/speech-bubble)
 *  archetype — smaller + quicker than combat text's 42px given the much
 *  shorter (150-500ms) lifetime. `0` for a ground-shape archetype below. */
const FLOAT_RISE_DISTANCE_PX = 20;

/** Spawn height above the unit anchor for a floating archetype, screen px —
 *  mirrors `CombatTextStyle.offsetAbovePx`'s role. */
const FLOAT_OFFSET_ABOVE_PX = 30;

/** Shield-outline pentagon half-width/height, screen px (Spell Reflect). */
const SHIELD_HALF_WIDTH_PX = 14;
const SHIELD_HEIGHT_PX = 20;

/** Speech-bubble box size, screen px (Battleshout). */
const BUBBLE_WIDTH_PX = 24;
const BUBBLE_HEIGHT_PX = 18;

function isFloatingArchetype(archetype: AbilityVisualDef["archetype"]): boolean {
  return archetype !== "groundCircle" && archetype !== "groundWedge";
}

function riseDistanceFor(archetype: AbilityVisualDef["archetype"]): number {
  return isFloatingArchetype(archetype) ? FLOAT_RISE_DISTANCE_PX : 0;
}

/** A pooled Graphics + Text pair, and the live instance bound to it (`null`
 *  when idle in the pool). */
interface Slot {
  readonly graphics: Graphics;
  readonly text: Text;
  instance: VisualInstance | null;
}

/** Where a spawn should anchor, in the two forms the wire offers it (#804):
 *  the `cast` state's `aimPoint`/`target` pair, or a `castFired` event's
 *  resolved `aimPoint` (no target id — for an entity aim the sim resolved that
 *  entity's position INTO the aim point). `resolveOrigin` reads whichever the
 *  visual def's `origin` asks for. */
interface SpawnAim {
  readonly aimPoint: { readonly x: number; readonly y: number } | null;
  readonly targetId: EntityId | null;
}

interface VisualInstance {
  readonly id: number;
  readonly def: AbilityVisualDef;
  /** World anchor, frozen at spawn (mirrors `Floater.worldX/worldY` — the
   *  indicator doesn't track a moving caster/target after it fires). */
  readonly worldX: number;
  readonly worldY: number;
  /** Facing sector at spawn — only meaningful for a `groundWedge`. */
  readonly facingSector: number;
  readonly spawnedMs: number;
}

/** Draw a simple stroked shield-OUTLINE pentagon (Spell Reflect) — a
 *  geometric stand-in, not a detailed icon, per the issue's explicit ask. */
function drawShieldOutline(g: Graphics, color: number): void {
  const w = SHIELD_HALF_WIDTH_PX;
  const h = SHIELD_HEIGHT_PX;
  g.moveTo(-w, -h / 2)
    .lineTo(w, -h / 2)
    .lineTo(w, h * 0.1)
    .lineTo(0, h / 2)
    .lineTo(-w, h * 0.1)
    .closePath()
    .stroke({ width: 2, color });
}

/** Draw a rounded-rect speech bubble + small tail (Battleshout). */
function drawSpeechBubble(g: Graphics, color: number): void {
  const w = BUBBLE_WIDTH_PX;
  const h = BUBBLE_HEIGHT_PX;
  g.roundRect(-w / 2, -h / 2, w, h, 4).fill({ color, alpha: 0.9 });
  g.moveTo(-4, h / 2 - 1)
    .lineTo(4, h / 2 - 1)
    .lineTo(0, h / 2 + 7)
    .closePath()
    .fill({ color, alpha: 0.9 });
}

export class AbilityVisualManager {
  private readonly layer: Container;
  private readonly slots: Slot[] = [];
  private nextId = 0;
  private readonly maxConcurrent: number;
  /** Last-observed `(slot, startTick)` cast key per entity — `null` while
   *  idle/uncasting. Absent entirely for an entity never seen. */
  private readonly lastCastKey = new Map<EntityId, string | null>();

  constructor(layer: Container, maxConcurrent = 32) {
    this.layer = layer;
    this.maxConcurrent = maxConcurrent;
  }

  /**
   * Spawn this snapshot's ability visuals: newly-started casts (the `cast`
   * edge) plus this tick's instant-cast `castFired` events. Call once per
   * arrived snapshot (mirrors `CombatTextManager.ingest`'s cadence) — NOT per
   * rendered frame, or an ongoing multi-tick cast would re-spawn its flash on
   * every interpolated frame.
   *
   * `isHidden` (#422 parity with `CombatTextManager`): an entity currently
   * hidden from the local viewer (enemy stealth) never spawns a visual —
   * otherwise a stealthed caster's cast flash would leak their position just
   * as much as drawing their body would. Applied IDENTICALLY on both spawn
   * paths (an instant-cast flash leaks a stealthed position exactly as much as
   * a cast-time one). Defaults to "never hidden" so callers that don't care
   * (tests) can omit it.
   */
  ingest(
    state: WireSimState,
    nowMs: number,
    isHidden: (e: WireEntity) => boolean = () => false,
  ): void {
    const entities = state.entities;
    const seen = new Set<EntityId>();
    for (const e of entities) {
      seen.add(e.entityId);
      const cast = e.cast;
      const curKey = cast === null ? null : `${String(cast.slot)}:${String(cast.startTick)}`;
      const prevKey = this.lastCastKey.get(e.entityId) ?? null;
      if (cast !== null && curKey !== prevKey && !isHidden(e)) {
        this.trySpawn(
          e,
          cast.slot,
          { aimPoint: cast.aimPoint, targetId: cast.target },
          entities,
          nowMs,
        );
      }
      this.lastCastKey.set(e.entityId, curKey);
    }
    // Prune entities no longer present this snapshot (death/leave) so the
    // key map doesn't grow unbounded across a long match.
    for (const id of this.lastCastKey.keys()) {
      if (!seen.has(id)) this.lastCastKey.delete(id);
    }
    // #804: instant casts. `startTick === firedTick` ⇔ the cast never occupied
    // a serialized snapshot as `cast` state, so the loop above provably never
    // saw it; anything longer already flashed at cast START and is skipped here
    // (that inequality IS the no-double-spawn guarantee — see the class doc).
    // The caster must be present in THIS snapshot: it supplies the championId
    // the visual def resolves from, the facing a `groundWedge` points along,
    // and the auras the #422 hide check reads.
    for (const ev of state.castEvents) {
      if (ev.startTick !== ev.firedTick) continue;
      const caster = entities.find((e) => e.entityId === ev.caster);
      if (caster === undefined || isHidden(caster)) continue;
      this.trySpawn(caster, ev.slot, { aimPoint: ev.aimPoint, targetId: null }, entities, nowMs);
    }
  }

  private trySpawn(
    caster: WireEntity,
    slot: number,
    aim: SpawnAim,
    entities: readonly WireEntity[],
    nowMs: number,
  ): void {
    const def = abilityVisualDefFor(caster.championId, slot);
    if (def === null) return;
    const anchor = this.resolveOrigin(def.origin, caster, aim, entities);
    const pooled = this.acquireSlot();
    const instance: VisualInstance = {
      id: this.nextId++,
      def,
      worldX: anchor.x,
      worldY: anchor.y,
      facingSector: caster.facing,
      spawnedMs: nowMs,
    };
    pooled.instance = instance;
    this.draw(pooled, instance);
  }

  /** Resolve the world anchor for a spawn:
   *   - `"caster"`   → the caster's own position.
   *   - `"aimPoint"` → the resolved ground point (#629): `cast.aimPoint` on the
   *     cast-edge path, the `castFired` event's own `aimPoint` on the instant
   *     path (#804) — the SAME sim-side point either way.
   *   - `"target"`   → the locked target entity's CURRENT position, then the
   *     resolved aim point (on the instant path that IS the target's position
   *     at resolve, since `aimX/aimY` re-read an entity aim), then the caster.
   *     The two fallbacks are defensive: every `"target"`-origin ability in the
   *     locked list is entity-aimed. */
  private resolveOrigin(
    origin: AbilityVisualDef["origin"],
    caster: WireEntity,
    aim: SpawnAim,
    entities: readonly WireEntity[],
  ): { readonly x: number; readonly y: number } {
    if (origin === "caster") return { x: caster.x, y: caster.y };
    const aimPoint = aim.aimPoint;
    const fallback =
      aimPoint === null ? { x: caster.x, y: caster.y } : { x: aimPoint.x, y: aimPoint.y };
    if (origin === "aimPoint") return fallback;
    if (aim.targetId === null) return fallback;
    const target = entities.find((e) => e.entityId === aim.targetId);
    return target === undefined ? fallback : { x: target.x, y: target.y };
  }

  /** Get a free slot, reusing an idle one; else make one; else evict oldest
   *  live instance — identical discipline to `CombatTextManager.
   *  acquireSlot`. */
  private acquireSlot(): Slot {
    for (const s of this.slots) {
      if (s.instance === null) return s;
    }
    if (this.slots.length < this.maxConcurrent) {
      const graphics = new Graphics();
      const text = new Text({
        text: "",
        style: { fontFamily: "sans-serif", fontSize: 16 },
        // #730: supersampled so the viewport cover zoom cannot blur it.
        resolution: WORLD_TEXT_RESOLUTION,
      });
      text.anchor.set(0.5, 0.5);
      graphics.visible = false;
      text.visible = false;
      this.layer.addChild(graphics);
      this.layer.addChild(text);
      const slot: Slot = { graphics, text, instance: null };
      this.slots.push(slot);
      return slot;
    }
    let oldest = this.slots[0];
    /* c8 ignore next 3 -- unreachable: maxConcurrent >= 1 guarantees a slot. */
    if (oldest === undefined) {
      throw new Error("AbilityVisualManager: acquireSlot called with zero slots at cap");
    }
    for (const s of this.slots) {
      const oi = oldest.instance;
      if (s.instance !== null && (oi === null || s.instance.id < oi.id)) {
        oldest = s;
      }
    }
    return oldest;
  }

  /** Draw the (local-origin) Graphics geometry + Text content for a
   *  freshly-spawned instance. Called exactly once per spawn — `update()`
   *  only repositions/re-alphas afterward, never redraws. */
  private draw(slot: Slot, instance: VisualInstance): void {
    const { graphics, text } = slot;
    graphics.clear();
    text.text = "";
    const def = instance.def;
    switch (def.archetype) {
      case "groundCircle": {
        const r = projectRadius(def.radiusUnits);
        graphics.circle(0, 0, r).fill({ color: def.color, alpha: def.alpha });
        graphics.visible = true;
        text.visible = false;
        break;
      }
      case "groundWedge": {
        const r = projectRadius(def.rangeUnits);
        // bp cosine → half-angle (radians). Presentation-only trig — this is
        // NOT hashed sim state, just a screen-space cosmetic draw.
        const halfAngle = Math.acos(def.arcCosThresholdBp / 10000);
        const dir = facingToScreenVec(instance.facingSector);
        const angle0 = Math.atan2(dir.sdy, dir.sdx);
        graphics
          .moveTo(0, 0)
          .arc(0, 0, r, angle0 - halfAngle, angle0 + halfAngle)
          .closePath()
          .fill({ color: def.color, alpha: def.alpha });
        graphics.visible = true;
        text.visible = false;
        break;
      }
      case "floatGlyph": {
        text.text = def.glyph;
        text.style.fill = def.color;
        graphics.visible = false;
        text.visible = true;
        break;
      }
      case "floatShield": {
        drawShieldOutline(graphics, def.color);
        graphics.visible = true;
        text.visible = false;
        break;
      }
      case "floatSpeechBubble": {
        drawSpeechBubble(graphics, def.color);
        text.text = def.glyph;
        text.style.fill = 0x1a1a1a;
        graphics.visible = true;
        text.visible = true;
        break;
      }
    }
  }

  /**
   * Animate every live instance to `nowMs` and reclaim expired ones.
   * Positions project the frozen world anchor through the camera `center`,
   * then apply the upward screen rise (floating archetypes only) + the
   * spawn offset. Ground-shape archetypes stay pinned at the anchor.
   */
  update(nowMs: number, center: ProjectionCenter): void {
    for (const s of this.slots) {
      const inst = s.instance;
      if (inst === null) continue;
      const frame = advanceAbilityVisual(
        inst.spawnedMs,
        nowMs,
        inst.def.durationMs,
        riseDistanceFor(inst.def.archetype),
      );
      if (frame.expired) {
        s.instance = null;
        s.graphics.visible = false;
        s.text.visible = false;
        continue;
      }
      const screen = worldToScreen(inst.worldX, inst.worldY, center);
      const floating = isFloatingArchetype(inst.def.archetype);
      const y = floating ? screen.sy - FLOAT_OFFSET_ABOVE_PX - frame.risePx : screen.sy;
      s.graphics.position.set(screen.sx, y);
      s.text.position.set(screen.sx, y);
      s.graphics.alpha = frame.opacity;
      s.text.alpha = frame.opacity;
    }
  }

  /** Live instance count (tests / diagnostics). */
  liveCount(): number {
    let n = 0;
    for (const s of this.slots) if (s.instance !== null) n++;
    return n;
  }

  /** Destroy all Pixi resources. */
  destroy(): void {
    for (const s of this.slots) {
      s.graphics.destroy();
      s.text.destroy();
    }
    this.slots.length = 0;
    this.lastCastKey.clear();
  }
}
